export const WORKBENCH_STATE_KEY = 'workbench-state';
export const WORKBENCH_RETURN_KEY = 'listing-workbench-return';

const PERSISTED_WORKBENCH_KEYS = [
  'currentStep', 'highestStep', 'stepReady',
  'batchJobId', 'savedListingId', 'activeListingGenerationTaskId',
  'batchJob',
  'listingDraftTitle', 'listingDraftBullets', 'listingDraftDescription', 'listingDraftSearchTerms', 'listingDraftQa',
  'keywordLibrary', 'businessContext', 'keywordUsePolicies', 'competitorRoles', 'marketDataKeywords', 'mcpKeywords', 'stBackendCandidates', 'selectedStKeywords',
  'stSelectionMode',
  'keywordConflictResult', 'lastDetectedConflicts',
  'complianceResult', 'scoringReport', 'revisionSuggestions', 'revisionSuggestionsMeta', 'revisionSuggestionsError', 'algorithmTips',
  'imageGenerationResults', 'imageGenerationStatus',
  'imageSelectedSlots', 'imageVariantStyle',
  'snapshotState', 'batchSnapshots', 'selectedCompetitorIndex', 'competitorAsinsIncluded',
  'productInfo', 'masterRevisionInstructions',
];

function hasListingDraftContent(snapshot = {}) {
  const title = snapshot.listingDraftTitle;
  const bullets = snapshot.listingDraftBullets;
  const description = snapshot.listingDraftDescription;
  const searchTerms = snapshot.listingDraftSearchTerms;
  const qa = snapshot.listingDraftQa;
  return Boolean(
    title?.titleEn
    || title?.titleOptions?.length
    || bullets?.bulletsEn?.length
    || description?.descriptionEn
    || searchTerms?.searchTermsEn
    || (Array.isArray(qa) && qa.length > 0)
  );
}

export function createWorkbenchStateController({
  getState,
  setState,
  setStateBulk,
  syncNavigationBounds,
  sessionStorageRef = globalThis.sessionStorage,
  localStorageRef = globalThis.localStorage,
} = {}) {
  function clearSessionArtifacts() {
    try {
      sessionStorageRef.removeItem(WORKBENCH_STATE_KEY);
      sessionStorageRef.removeItem(WORKBENCH_RETURN_KEY);
    } catch { /* ignore */ }
  }

  function clearListingTaskPersistence() {
    try {
      localStorageRef.removeItem('listing_currentStep');
      localStorageRef.removeItem('listing_highestStep');
      localStorageRef.removeItem('listing_savedListingId');
      sessionStorageRef.removeItem('listing_batchJobId');
      sessionStorageRef.removeItem('listing_activeListingGenerationTaskId');
    } catch { /* ignore */ }
  }

  function save() {
    const snapshot = {};
    for (const key of PERSISTED_WORKBENCH_KEYS) {
      const value = getState(key);
      if (value !== undefined && value !== null) {
        snapshot[key] = value instanceof Set ? [...value] : value;
      }
    }
    try {
      sessionStorageRef.setItem(WORKBENCH_STATE_KEY, JSON.stringify(snapshot));
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  function restore() {
    const raw = sessionStorageRef.getItem(WORKBENCH_STATE_KEY);
    if (!raw) return false;
    sessionStorageRef.removeItem(WORKBENCH_STATE_KEY);
    try {
      const snapshot = JSON.parse(raw);
      const shouldOpenListingDraft = hasListingDraftContent(snapshot);
      if (snapshot.selectedStKeywords) {
        snapshot.selectedStKeywords = new Set(snapshot.selectedStKeywords);
      }
      if (snapshot.competitorAsinsIncluded) {
        snapshot.competitorAsinsIncluded = new Set(snapshot.competitorAsinsIncluded);
      }
      setStateBulk(snapshot);
      syncNavigationBounds();
      if (shouldOpenListingDraft && getState('currentStep') < 3) {
        setState('currentStep', 3);
        syncNavigationBounds();
      }
      return true;
    } catch {
      return false;
    }
  }

  function initAssetsMode() {
    setStateBulk({
      workflowMode: 'assets',
      currentStep: 1,
      highestStep: 7,
      stepReady: {},
      batchJobId: null,
      keywordLibrary: null,
    });
  }

  return {
    clearSessionArtifacts,
    clearListingTaskPersistence,
    save,
    restore,
    initAssetsMode,
  };
}
